// Counter-scales the app for OS display scaling and browser zoom, replacing the
// fixed per-DPI zoom tiers that used to live in dpi.css. The value is exposed as
// the `--app-zoom` CSS variable, which dpi.css applies to .container/.modal.
const MIN_ZOOM = 50;
const MAX_ZOOM = 100;

// Desktop macOS only. iPad in desktop mode also reports "MacIntel" but has touch
// points; Android/iOS/Windows/Linux keep the devicePixelRatio logic.
const isMac = (() => {
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  return /mac/i.test(platform) && !(navigator.maxTouchPoints > 1);
})();

// Browser zoom steps offered by Chrome and Safari
const BROWSER_ZOOM_LEVELS = [
  0.25, 0.33, 0.5, 0.63, 0.67, 0.75, 0.8, 0.85, 0.9, 1, 1.1, 1.15, 1.25, 1.5, 1.75, 2, 2.5, 3,
];
const ZOOM_SNAP_TOLERANCE = 0.02;

// On macOS devicePixelRatio is 2 on Retina regardless of browser zoom in Safari, so it
// can't be used there. Browser zoom shrinks innerWidth (CSS px) but not outerWidth
// (screen px), so their ratio gives the zoom. It is snapped to a real zoom step, and
// anything else (side panel, docked devtools) is treated as 100%.
const getMacBrowserZoom = () => {
  if (!window.innerWidth || !window.outerWidth) return 1;
  const ratio = window.outerWidth / window.innerWidth;
  const level = BROWSER_ZOOM_LEVELS.find((l) => Math.abs(ratio - l) / l <= ZOOM_SNAP_TOLERANCE);
  return level || 1;
};

// Windows/Linux: devicePixelRatio = OS display scaling × browser zoom, counter both.
// macOS: no fractional OS scaling to counter, only browser zoom.
const getScale = () => (isMac ? getMacBrowserZoom() : window.devicePixelRatio || 1);

export const applyAppZoom = () => {
  const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, (1 / getScale()) * 100));

  document.documentElement.style.setProperty('--app-zoom', `${zoom}%`);
};

export const initAppZoom = () => {
  applyAppZoom();
  // Browser zoom changes devicePixelRatio / innerWidth and fires `resize`
  window.addEventListener('resize', applyAppZoom);
};

// Effective CSS zoom applied to `node` by its zoomed ancestors (1 when unzoomed).
export const getEffectiveZoom = (node) => {
  if (!node || !node.offsetWidth) return 1;
  return node.getBoundingClientRect().width / node.offsetWidth || 1;
};

// dnd-kit measures in screen pixels, but a transform inside a CSS-zoomed element is
// scaled by that zoom again. Returns a translate that keeps `node` under the cursor.
export const getZoomSafeTranslate = (node, transform, { lockX = false } = {}) => {
  if (!transform) return undefined;
  const zoom = getEffectiveZoom(node);
  const x = lockX ? 0 : transform.x / zoom;
  return `translate3d(${x}px, ${transform.y / zoom}px, 0)`;
};
